/**
 * Iter 67bu — Partner Dashboard.
 *
 * Serves two related-but-distinct partner shapes:
 *
 *   (a) "vendor_staff" external agents (iter 67bu/bv) — tied to a single host,
 *       earn a % of the *rental total* on their host's bookings only.
 *       Endpoints: /vendor/referrals/summary, /vendor/referrals/ledger
 *
 *   (b) Platform-level partners (iter 67cs) — `role="partner"`, hotels/agencies/
 *       concierges that work across every vendor. Earn a % of the *platform
 *       service fee* on any booking they bring in.
 *       Endpoints: /partner/me, /partner/summary, /partner/bookings
 *
 * The two shapes are normalised into a single render path so the UI stays
 * one component. Platform partners additionally get a small country-scoped
 * link builder so e.g. a Rome hotel can hand out a link pre-filtered to
 * Italy.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Money from "@/components/Money";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Copy, ExternalLink, BadgeCheck, TrendingUp, Wallet, Link2, Globe2 } from "lucide-react";
import { toast } from "sonner";

function _origin() {
  if (typeof window === "undefined") return "";
  return window.location.origin;
}

export default function PartnerDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);         // normalised { profile, ledger }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Country list for the link builder (platform partners only).
  const [countries, setCountries] = useState([]);
  const [linkCountry, setLinkCountry] = useState("");

  const isPlatformPartner = user?.role === "partner";
  const isVendorAgent = user?.role === "vendor_staff" && user?.staff_role === "external_agent";

  useEffect(() => {
    if (!user) return;
    if (!isPlatformPartner && !isVendorAgent) return;
    setLoading(true);
    setError(null);

    if (isPlatformPartner) {
      Promise.all([
        api.get("/partner/me"),
        api.get("/partner/summary"),
        api.get("/partner/bookings"),
      ]).then(([me, sum, bk]) => {
        const profile = {
          name: me.data.business_name || me.data.name,
          contact_name: me.data.name,
          referral_code: me.data.referral_code,
          referral_percent: Number(me.data.effective_commission_percent || 0),
          commission_total_eur: sum.data.commission_total_eur,
          bookings_earned: sum.data.earned?.count || 0,
          bookings_total: sum.data.total_bookings || 0,
          kind: "partner",
          partner_kind: me.data.partner_kind,
          country: me.data.country || null,
          // Iter 67cs — partners earn off the platform fee, not the rental.
          commission_base_label: "platform service fee",
        };
        // Normalise the booking ledger into the same shape the existing
        // vendor-staff renderer expects.
        const ledger = (bk.data || []).map((r) => ({
          id: r.id,
          customer_name: r.customer_email || "—",
          start_date: r.start_date,
          end_date: r.end_date,
          rental_total: r.rental_total || r.amount,
          referral_commission_amount: r.referral_commission_amount || 0,
          payout_status: r.status === "completed" ? "earned"
                        : r.status === "cancelled" ? "lost"
                        : "pending",
        }));
        setData({ profile, ledger });
      }).catch((e) => {
        setError(e?.response?.data?.detail || "Couldn't load partner data");
      }).finally(() => setLoading(false));
      // Populate country list for the link builder.
      api.get("/locations/countries")
        .then((r) => setCountries(r.data || []))
        .catch(() => {});
    } else {
      Promise.all([
        api.get("/vendor/referrals/summary").catch(() => ({ data: { rows: [] } })),
        api.get("/vendor/referrals/ledger").catch(() => ({ data: { rows: [] } })),
      ]).then(([s, l]) => {
        const rows = s.data?.rows || [];
        const me = rows.find((r) => r.id === user.id) || rows[0] || {};
        const profile = {
          name: me.name || user.name,
          contact_name: user.name,
          referral_code: me.referral_code,
          referral_percent: Number(me.referral_percent || 0),
          commission_total_eur: me.commission_total_eur || 0,
          bookings_earned: me.bookings_earned || 0,
          bookings_total: me.bookings_total || 0,
          kind: "vendor_staff",
          commission_base_label: "rental total",
        };
        setData({ profile, ledger: l.data?.rows || [] });
      }).catch((e) => {
        setError(e?.response?.data?.detail || "Couldn't load partner data");
      }).finally(() => setLoading(false));
    }
    // Default the country link to the partner's preferred country if set.
    if (isPlatformPartner) {
      api.get("/partner/me").then((r) => {
        if (r.data?.country) setLinkCountry(r.data.country);
      }).catch(() => {});
    }
  }, [user, isPlatformPartner, isVendorAgent]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!isPlatformPartner && !isVendorAgent) {
    return <Navigate to="/" replace />;
  }

  const me = data?.profile;
  const ledger = data?.ledger || [];
  const baseUrl = me?.referral_code ? `${_origin()}/cars?ref=${encodeURIComponent(me.referral_code)}` : null;
  const countryUrl = me?.referral_code && linkCountry
    ? `${_origin()}/cars?country=${encodeURIComponent(linkCountry)}&ref=${encodeURIComponent(me.referral_code)}`
    : null;

  const copy = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Referral link copied!"),
      () => toast.error("Could not copy link"),
    );
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="partner-dashboard">
        <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white shadow-md shadow-pink-200">
              <BadgeCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-pink-700" data-testid="partner-kind-label">
                {isPlatformPartner ? `Platform partner · ${me?.partner_kind || "partner"}` : "Referral partner"}
              </div>
              <h1 className="font-display text-3xl tracking-tight font-medium">Welcome, {me?.name || user.name}</h1>
              <div className="text-sm text-neutral-500">
                Earn <strong>{me?.referral_percent ?? "—"}%</strong> commission of the {me?.commission_base_label || "rental total"} on every booking through your link.
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl bg-white border border-border p-16 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
          </div>
        ) : error || !me ? (
          <div className="rounded-2xl bg-white border border-border p-10 text-center text-neutral-500" data-testid="partner-load-error">
            {error || "Couldn't load your partner profile."}
          </div>
        ) : (
          <>
            {/* Primary referral link */}
            <div className="rounded-2xl bg-gradient-to-br from-white via-pink-50/40 to-rose-50/40 ring-1 ring-pink-200 p-6 mb-6" data-testid="partner-link-card">
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="h-4 w-4 text-pink-700" />
                <span className="text-[10px] uppercase tracking-wider font-bold text-pink-900">Your unique referral link</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="text"
                  value={baseUrl || ""}
                  readOnly
                  onFocus={(e) => e.target.select()}
                  className="flex-1 min-w-[280px] px-3 py-2.5 rounded-xl bg-white ring-1 ring-pink-200 text-sm font-mono tabular-nums text-pink-900"
                  data-testid="partner-link-input"
                />
                <Button onClick={() => copy(baseUrl)} className="rounded-full bg-pink-600 hover:bg-pink-700 text-white" data-testid="partner-link-copy">
                  <Copy className="h-4 w-4 mr-1.5" /> Copy
                </Button>
                {baseUrl && (
                  <a href={baseUrl} target="_blank" rel="noopener noreferrer" data-testid="partner-link-open">
                    <Button variant="outline" className="rounded-full">
                      <ExternalLink className="h-4 w-4 mr-1.5" /> Open
                    </Button>
                  </a>
                )}
              </div>
              <div className="mt-3 text-xs text-neutral-600 leading-relaxed">
                Share this link via WhatsApp, email, or print it on your reception desk. Every booking made through it is automatically tagged to you — no codes for your guests to type.
              </div>
              <div className="mt-3 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-pink-100 text-pink-900 ring-1 ring-pink-200 text-[11px] font-mono font-bold" data-testid="partner-code-chip">
                Code · {me.referral_code}
              </div>
            </div>

            {/* Iter 67cs — country-scoped link builder (platform partners only). */}
            {isPlatformPartner && countries.length > 0 && (
              <div className="rounded-2xl bg-white ring-1 ring-neutral-200 p-6 mb-6" data-testid="partner-country-link-card">
                <div className="flex items-center gap-2 mb-3">
                  <Globe2 className="h-4 w-4 text-indigo-700" />
                  <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-700">Country-scoped link</span>
                </div>
                <p className="text-xs text-neutral-500 mb-3">
                  Hand guests a link that opens RentalCar.al pre-filtered to a single country — perfect for a Rome hotel pointing only to Italy, or a Tirana concierge to Albania.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={linkCountry} onValueChange={setLinkCountry}>
                    <SelectTrigger className="w-48" data-testid="partner-country-select">
                      <SelectValue placeholder="Choose country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={countryUrl || ""}
                    readOnly
                    onFocus={(e) => e.target.select()}
                    placeholder="Pick a country above to generate"
                    className="flex-1 min-w-[280px] font-mono text-sm"
                    data-testid="partner-country-link-input"
                  />
                  <Button onClick={() => copy(countryUrl)} disabled={!countryUrl} variant="outline" className="rounded-full" data-testid="partner-country-link-copy">
                    <Copy className="h-4 w-4 mr-1.5" /> Copy
                  </Button>
                </div>
              </div>
            )}

            {/* Earnings stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <_StatCard
                tone="emerald"
                icon={Wallet}
                label="Commission earned"
                value={<Money amount={me.commission_total_eur || 0} />}
                hint="From confirmed + completed bookings"
                testId="partner-stat-commission"
              />
              <_StatCard
                tone="blue"
                icon={TrendingUp}
                label="Earning bookings"
                value={me.bookings_earned || 0}
                hint="Confirmed via your link"
                testId="partner-stat-earned"
              />
              <_StatCard
                tone="neutral"
                icon={BadgeCheck}
                label="Total referrals"
                value={me.bookings_total || 0}
                hint="Includes pending + cancelled"
                testId="partner-stat-total"
              />
            </div>

            <div className="rounded-2xl bg-white border border-border p-6">
              <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-2">How earnings work</div>
              <ul className="space-y-1.5 text-sm text-neutral-700">
                <li className="flex gap-2"><span className="text-emerald-600">•</span>You earn <strong>{me.referral_percent}%</strong> of each booking's {me.commission_base_label}.</li>
                <li className="flex gap-2"><span className="text-emerald-600">•</span>Commission is credited when the booking is <strong>confirmed</strong>.</li>
                <li className="flex gap-2"><span className="text-rose-500">•</span>Cancelled or refunded bookings don't count toward earnings.</li>
                <li className="flex gap-2"><span className="text-neutral-400">•</span>Pay-out frequency and method are agreed directly with RentalCar.al{isVendorAgent ? " (or your host)" : ""}.</li>
              </ul>
            </div>

            {/* Per-booking ledger */}
            <div className="mt-6 rounded-2xl bg-white border border-border overflow-hidden" data-testid="partner-ledger">
              <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                <div>
                  <h2 className="font-display text-xl font-semibold tracking-tight">Booking ledger</h2>
                  <p className="text-xs text-neutral-500">Every booking that came through your link, newest first.</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">{ledger.length} {ledger.length === 1 ? "row" : "rows"}</span>
              </div>
              {ledger.length === 0 ? (
                <div className="p-10 text-center text-sm text-neutral-500" data-testid="partner-ledger-empty">
                  No referrals yet. Share your link to start earning.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50/60 text-[10px] uppercase tracking-wider text-neutral-500">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-bold">Booking</th>
                        <th className="text-left px-4 py-2.5 font-bold">Customer</th>
                        <th className="text-left px-4 py-2.5 font-bold">Dates</th>
                        <th className="text-right px-4 py-2.5 font-bold">Rental</th>
                        <th className="text-right px-4 py-2.5 font-bold">Your commission</th>
                        <th className="text-left px-4 py-2.5 font-bold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.map((r) => {
                        const tone = r.payout_status === "earned"
                          ? "bg-emerald-100 text-emerald-800 ring-emerald-200"
                          : r.payout_status === "lost"
                            ? "bg-rose-100 text-rose-800 ring-rose-200"
                            : "bg-amber-100 text-amber-800 ring-amber-200";
                        const label = r.payout_status === "earned"
                          ? "Earned"
                          : r.payout_status === "lost"
                            ? "Lost"
                            : "Pending";
                        return (
                          <tr key={r.id} className="border-t border-border" data-testid={`partner-ledger-row-${r.id}`}>
                            <td className="px-4 py-3 font-mono text-[11px] text-neutral-600">#{(r.id || "").slice(0, 8)}</td>
                            <td className="px-4 py-3 text-neutral-900">{r.customer_name || "—"}</td>
                            <td className="px-4 py-3 text-xs text-neutral-600 tabular-nums">{(r.start_date || "").slice(0, 10)} → {(r.end_date || "").slice(0, 10)}</td>
                            <td className="px-4 py-3 text-right tabular-nums"><Money amount={r.rental_total} /></td>
                            <td className="px-4 py-3 text-right tabular-nums font-semibold text-emerald-700"><Money amount={r.referral_commission_amount} /></td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ring-1 ${tone} text-[10px] font-bold uppercase tracking-wider`}>{label}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function _StatCard({ icon: Icon, label, value, hint, tone, testId }) {
  const tones = {
    emerald: "bg-emerald-50 ring-emerald-200 text-emerald-900",
    blue:    "bg-blue-50 ring-blue-200 text-blue-900",
    neutral: "bg-white ring-neutral-200 text-neutral-900",
  };
  const iconTones = {
    emerald: "bg-emerald-100 text-emerald-700",
    blue:    "bg-blue-100 text-blue-700",
    neutral: "bg-neutral-100 text-neutral-700",
  };
  return (
    <div className={`rounded-2xl ring-1 p-5 ${tones[tone] || tones.neutral}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`h-7 w-7 rounded-lg flex items-center justify-center ${iconTones[tone] || iconTones.neutral}`}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      </div>
      <div className="text-3xl font-bold tabular-nums">{value}</div>
      <div className="text-xs opacity-70 mt-1">{hint}</div>
    </div>
  );
}
